import React from "react";
import { SmetaSplitterElement } from "../../../ComponentInterface";

export type Side = "left" | "right" | "top" | "bottom";
export interface SmetaSplitterProps {
  id?: string;
  style?: React.CSSProperties;
  /** Ориентация сплиттера vertical | horizontal.
   * @default "horizontal"
   */
  orientation?: "vertical" | "horizontal";
  /** Индекс элемента который не коллапсится(start = левый/верхний; end = правый/нижний).
   * @default "start"
   */
  primaryIndex?: "start" | "end";
  /** Функция, которая будет вызвана после изменений размеров элемента. */
  onResizeEnd?: (splitterEl: SmetaSplitterElement) => void;
  /** Функция, которая будет вызвана после закрытия сплиттера. При контролируемом компоненте будет вызвана без закрытия. */
  onCollapse?: (splitterEl: SmetaSplitterElement) => void;
  /** Функция, которая будет вызвана после распахивания сплиттера. При контролируемом компоненте будет вызвана без распахивания. */
  onExpand?: (splitterEl: SmetaSplitterElement) => void;
  /** Время анимации в ms.
   * @default 250
   */
  transitionDuration?: number;
  /** Блокировка изменений размеров пользователем. */
  blockResize?: boolean;
  /** Скрытие кнопок collapse и expand. */
  hideButtons?: boolean;
  /** Скрыть разделитель. */
  hideSplitter?: boolean;
  /** Скрыть разделитель когда сплиттер свернут. */
  hideSplitterCollapsed?: boolean;
  /** ref к которому присвоится ссылка на сплиттер. */
  splitterRef?: React.RefObject<SmetaSplitterElement>;
  /** Сохранить пропорции при изменении размера окна.
   * @default true
   */
  keepProportionsOnResize?: boolean;
  children: React.ReactNode;
}
export interface SmetaSplitterItemProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  size?: string;
  min?: string;
  max?: string;
  /** Значение открытый или закрытый сплиттер. Используется при контролируемом компоненте. */
  collapsed?: boolean;
  /** Значение открытый или закрытый сплиттер. Используется при неконтролируемом компоненте. */
  defaultCollapsed?: boolean;
  collapseSide?: Side;
}
export interface SmetaSplitterBarProps {
  hideSplitter?: boolean;
  blockResize?: boolean;
  hideButtons?: boolean;
  primaryIndex: "start" | "end";
  collapseSide: Side;
}
export interface Measurements {
  dimension: "width" | "height";
  minDimension: "minWidth" | "minHeight";
  maxDimension: "maxWidth" | "maxHeight";
  restrictedDimension: "Height" | "Width";
  size: "offsetWidth" | "offsetHeight";
  offset: "offsetLeft" | "offsetTop";
  position: "left" | "top";
  position2: "right" | "bottom";
  pagePosition: "pageX" | "pageY";
  scroll: "scrollLeft" | "scrollTop";
}
export type SmetaSplitterItemLimits = {
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  ignoreUpdate?: boolean;
};
export type ObjectDragDetails = {
  firstItem: number;
  lockedItemsSize: number;
  position: number;
  scrollAmount: number;
  secondItem: number;
  secondItemTotalMaxSize: number;
  secondItemTotalMinSize: number;
  splitAreaSize: number;
  splitBarOffset: number;
};
export const smetaSplitterBarSize = 4;
export const getMeasurements = (orientation?: "vertical" | "horizontal"): Measurements => {
  if (orientation === "vertical")
    return {
      dimension: "width",
      minDimension: "minWidth",
      maxDimension: "maxWidth",
      restrictedDimension: "Height",
      size: "offsetWidth",
      offset: "offsetLeft",
      position: "left",
      position2: "right",
      pagePosition: "pageX",
      scroll: "scrollLeft",
    };
  return {
    dimension: "height",
    minDimension: "minHeight",
    maxDimension: "maxHeight",
    restrictedDimension: "Width",
    size: "offsetHeight",
    offset: "offsetTop",
    position: "top",
    position2: "bottom",
    pagePosition: "pageY",
    scroll: "scrollTop",
  };
};
export const getChildren = (children: React.ReactNode): null | React.ReactNode[] => {
  const childrenArray = React.Children.toArray(children);
  return childrenArray.length ? childrenArray : null;
};
export const getDefaultCollapseSide = (primaryIndex: "start" | "end", orientation?: "vertical" | "horizontal") => {
  if (orientation === "vertical") return primaryIndex === "start" ? "right" : "left";
  return primaryIndex === "start" ? "bottom" : "top";
};
